import { useEffect } from 'react';
import site from '../config/site.json';

// The one place the production origin is set. Canonicals, social tags and structured data read it
// here, and scripts/prerender.js uses it for sitemap.xml and robots.txt. When the custom domain goes
// live, change src/config/site.json and rebuild.
export const SITE_URL = site.url;

const DEFAULT_TITLE = 'Sidwell — AI Financial Analysis for Advisory Firms';
const DEFAULT_DESCRIPTION =
  'Ask a client’s financial data a question in plain English. Sidwell runs the maths inside their own database and shows how every figure was worked out.';
const OG_IMAGE = `${SITE_URL}/og-image.png`;

const SITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Sidwell',
      legalName: 'Sidwell Intelligence Systems Inc.',
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/brand/sidwell-wordmark-on-light.png`,
      description: DEFAULT_DESCRIPTION,
      email: 'hello@sidwell.ai',
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'Sidwell',
      url: `${SITE_URL}/`,
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ],
};

function upsertMeta(key, keyValue, content) {
  let el = document.head.querySelector(`meta[${key}="${keyValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, keyValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Sets <title>, description, canonical, robots, social tags and structured data for a route. Each
 * page calls this on mount, and scripts/prerender.js captures the result into that route's static
 * HTML, so crawlers that don't run JavaScript still get the right tags.
 */
export default function useSeo({ title, description, path = '', noindex = false, schema } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} — Sidwell` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const url = `${SITE_URL}${path}`;

    document.title = fullTitle;
    upsertMeta('name', 'description', desc);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Sidwell');
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', OG_IMAGE);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:image:alt', fullTitle);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', OG_IMAGE);
    // Utility routes (sign-in, 404) stay out of the index, so they don't declare a canonical either.
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    setCanonical(noindex ? null : url);
    setJsonLd('schema-site', SITE_SCHEMA);
    setJsonLd('schema-page', schema || null);
  }, [title, description, path, noindex, schema]);
}
